import React, { useMemo, useState } from 'react';
import { ChevronDown, RefreshCw } from 'lucide-react';
import PropTypes from 'prop-types';
import { IMaskInput } from 'react-imask';
import { NumericFormat } from 'react-number-format';

import { useToast } from '../../../../components/ui/toast/ToastContext';
import { camelizeKeys } from '../../../../utils/camelizeKeys';
import { fetchWithCsrf } from '../../../../utils/fetchWithCsrf';
import {
  accountTypeOptions,
  areaLabels,
  companyTypeOptions,
  jobTitleLabels,
  profileArea,
  transferIntervalOptions,
} from './accountConstants';

const fieldClassName = (error) =>
  `h-11 w-full rounded-xl border bg-white px-3 text-sm text-blueDarker outline-none transition-colors placeholder-bluePale focus:ring-1 ${
    error
      ? 'border-redHighlight focus:border-redHighlight focus:ring-redHighlight'
      : 'border-grayExtraLight2 focus:border-tealMedium focus:ring-tealMedium'
  }`;

const Field = ({ label, name, error, hint, children }) => (
  <div className="flex min-w-0 flex-col gap-2">
    <label htmlFor={name} className="text-xs font-semibold text-blueMedium">
      {label}
    </label>
    {children}
    {hint && !error && <span className="text-xs leading-4 text-bluePale">{hint}</span>}
    {error && <span className="text-xs leading-4 text-redBase">{error}</span>}
  </div>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  error: PropTypes.string,
  hint: PropTypes.string,
  children: PropTypes.node.isRequired,
};

Field.defaultProps = {
  error: '',
  hint: '',
};

const TextField = ({ label, name, value, onChange, placeholder, error, type, maxLength, disabled, hint }) => (
  <Field label={label} name={name} error={error} hint={hint}>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      maxLength={maxLength}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className={`${fieldClassName(error)} ${disabled ? 'cursor-not-allowed bg-blueLight text-bluePale' : ''}`}
    />
  </Field>
);

TextField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  type: PropTypes.string,
  maxLength: PropTypes.number,
  disabled: PropTypes.bool,
  hint: PropTypes.string,
};

TextField.defaultProps = {
  placeholder: '',
  error: '',
  type: 'text',
  maxLength: undefined,
  disabled: false,
  hint: '',
};

const MaskedField = ({ label, name, value, onChange, placeholder, error, mask }) => (
  <Field label={label} name={name} error={error}>
    <IMaskInput
      id={name}
      name={name}
      value={String(value || '')}
      mask={mask}
      placeholder={placeholder}
      onAccept={(nextValue) => onChange(nextValue)}
      className={fieldClassName(error)}
    />
  </Field>
);

MaskedField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  mask: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
};

MaskedField.defaultProps = {
  placeholder: '',
  error: '',
};

const DateField = ({ value, onChange, error }) => {
  const displayValue = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.split('-').reverse().join('/') : value || '';

  return (
    <MaskedField
      label="Data de nascimento"
      name="birthDate"
      value={displayValue}
      onChange={(nextValue) => {
        const [day, month, year] = nextValue.split('/');
        onChange(day && month && year?.length === 4 ? `${year}-${month}-${day}` : nextValue);
      }}
      placeholder="DD/MM/AAAA"
      error={error}
      mask="00/00/0000"
    />
  );
};

DateField.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

DateField.defaultProps = {
  error: '',
};

const SelectField = ({ label, name, value, onChange, options, placeholder, error, disabled, hint }) => (
  <Field label={label} name={name} error={error} hint={hint}>
    <div className="relative">
      <select
        id={name}
        name={name}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className={`${fieldClassName(error)} appearance-none pr-10 ${
          disabled ? 'cursor-not-allowed bg-blueLight text-bluePale' : 'cursor-pointer'
        }`}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-blueSteel">
        <ChevronDown size={17} strokeWidth={1.7} />
      </span>
    </div>
  </Field>
);

SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired })
  ).isRequired,
  placeholder: PropTypes.string.isRequired,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  hint: PropTypes.string,
};

SelectField.defaultProps = {
  error: '',
  disabled: false,
  hint: '',
};

const CurrencyField = ({ value, onChange, error }) => (
  <Field label="Faturamento/Renda mensal" name="incomeValue" error={error}>
    <NumericFormat
      id="incomeValue"
      name="incomeValue"
      value={value}
      thousandSeparator="."
      decimalSeparator=","
      decimalScale={2}
      fixedDecimalScale
      allowNegative={false}
      prefix="R$ "
      placeholder="R$ 0,00"
      onValueChange={({ floatValue }) => onChange(floatValue ?? '')}
      className={fieldClassName(error)}
    />
  </Field>
);

CurrencyField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

CurrencyField.defaultProps = {
  error: '',
};

const PercentageField = ({ label, name, value, onChange, error }) => (
  <Field label={label} name={name} error={error}>
    <NumericFormat
      id={name}
      name={name}
      value={value}
      decimalSeparator=","
      decimalScale={2}
      allowNegative={false}
      suffix=" %"
      placeholder="0,0"
      isAllowed={({ floatValue }) => floatValue === undefined || floatValue <= 100}
      onValueChange={({ floatValue }) => onChange(floatValue ?? '')}
      className={fieldClassName(error)}
    />
  </Field>
);

PercentageField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

PercentageField.defaultProps = {
  error: '',
};

const SectionTitle = ({ children }) => (
  <h3 className="col-span-full text-xs font-semibold uppercase tracking-wide text-bluePale">{children}</h3>
);

SectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
};

const secureRandomIndex = (length) => {
  if (window.crypto?.getRandomValues) {
    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    return values[0] % length;
  }

  return Math.floor(Math.random() * length);
};

const generateTemporaryPassword = () => {
  const groups = ['abcdefghijkmnopqrstuvwxyz', 'ABCDEFGHJKLMNPQRSTUVWXYZ', '23456789', '!@#$%&*'];
  const allCharacters = groups.join('');
  const characters = groups.map((group) => group[secureRandomIndex(group.length)]);

  while (characters.length < 10) characters.push(allCharacters[secureRandomIndex(allCharacters.length)]);

  for (let index = characters.length - 1; index > 0; index -= 1) {
    const swapIndex = secureRandomIndex(index + 1);
    [characters[index], characters[swapIndex]] = [characters[swapIndex], characters[index]];
  }

  return characters.join('');
};

const emptyBankAccount = {
  id: null,
  name: '',
  documentNumber: '',
  companyType: '',
  transferInterval: '',
  transferDay: '',
  accountType: '',
  bankCode: '',
  bankAg: '',
  agenciaDv: '',
  bankCc: '',
  contaDv: '',
};

const buildInitialForm = (employee, mode) => ({
  fullName: employee?.fullName || '',
  email: employee?.email || '',
  cpf: employee?.cpf || '',
  birthDate: employee?.birthDate || '',
  phone: employee?.phone || '',
  incomeValue: employee?.incomeValue ?? '',
  area: profileArea(employee?.accessProfile),
  accessProfileId: employee?.accessProfile?.id ? String(employee.accessProfile.id) : '',
  password: mode === 'create' ? generateTemporaryPassword() : '',
  postalCode: employee?.address?.postalCode || '',
  address: employee?.address?.street || '',
  addressNumber: employee?.address?.number || '',
  province: employee?.address?.province || '',
  city: employee?.address?.city || '',
  state: employee?.address?.state || '',
  commissionsRecurrent: employee?.commission?.recurrent ?? 0,
  commissionsSubscription: employee?.commission?.subscription ?? 0,
  bankAccount: { ...emptyBankAccount, ...(employee?.bankAccount || {}) },
});

const splitName = (fullName) => {
  const [firstName, ...lastNameParts] = fullName.trim().replace(/\s+/g, ' ').split(' ');
  return { firstName, lastName: lastNameParts.join(' ') };
};

const roleForProfile = (profile, employee) => {
  if (['analyst', 'planner'].includes(profile?.jobTitle)) return profile.jobTitle;
  return employee?.role || 'planner';
};

const validateForm = (form, mode) => {
  const errors = {};
  const bank = form.bankAccount;
  const documentDigits = String(bank.documentNumber || '').replace(/\D/g, '');

  if (form.fullName.trim().split(/\s+/).length < 2) errors.fullName = 'Informe nome e sobrenome.';
  if (!/^\S+@\S+\.\S+$/.test(form.email)) errors.email = 'Informe um e-mail válido.';
  if (!form.birthDate || !/^\d{4}-\d{2}-\d{2}$/.test(form.birthDate)) errors.birthDate = 'Informe uma data válida.';
  if (!form.phone) errors.phone = 'Informe o telefone.';
  if (form.incomeValue === '') errors.incomeValue = 'Informe a renda mensal.';
  if (!form.area) errors.area = 'Selecione a área.';
  if (!form.accessProfileId) errors.accessProfileId = 'Selecione o cargo.';
  if (mode === 'create' && !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/.test(form.password)) {
    errors.password = 'Use ao menos 8 caracteres, com maiúscula, minúscula, número e símbolo.';
  }

  if (!form.postalCode) errors.postalCode = 'Informe o CEP.';
  if (!form.address) errors.address = 'Informe o logradouro.';
  if (!form.addressNumber) errors.addressNumber = 'Informe o número.';
  if (!form.province) errors.province = 'Informe o bairro.';
  if (!form.city) errors.city = 'Informe a cidade.';
  if (!form.state) errors.state = 'Informe a UF.';
  if (form.commissionsRecurrent === '') errors.commissionsRecurrent = 'Informe a comissão recorrente.';
  if (form.commissionsSubscription === '') errors.commissionsSubscription = 'Informe a comissão de inscrição.';

  if (!bank.name) errors.bankName = 'Informe o titular da conta.';
  if (!bank.documentNumber) errors.documentNumber = 'Informe o CPF ou CNPJ do titular.';
  if (documentDigits.length === 14 && !bank.companyType) errors.companyType = 'Selecione o tipo da empresa.';
  if (!bank.transferInterval) errors.transferInterval = 'Selecione o intervalo.';
  if (bank.transferInterval !== 'daily' && !bank.transferDay) errors.transferDay = 'Informe o dia da transferência.';
  if (bank.transferInterval === 'weekly' && (+bank.transferDay < 1 || +bank.transferDay > 5)) {
    errors.transferDay = 'Para repasse semanal, use um dia de 1 a 5.';
  }
  if (bank.transferInterval === 'monthly' && (+bank.transferDay < 1 || +bank.transferDay > 31)) {
    errors.transferDay = 'Para repasse mensal, use um dia de 1 a 31.';
  }
  if (!bank.accountType) errors.accountType = 'Selecione o tipo da conta.';
  if (!bank.bankCode) errors.bankCode = 'Informe o código do banco.';
  if (!bank.bankAg) errors.bankAg = 'Informe a agência.';
  if (!bank.bankCc) errors.bankCc = 'Informe o número da conta.';

  return errors;
};

const errorTab = (errors) => {
  const personalFields = [
    'fullName',
    'email',
    'birthDate',
    'phone',
    'incomeValue',
    'area',
    'accessProfileId',
    'password',
  ];
  const addressFields = [
    'postalCode',
    'address',
    'addressNumber',
    'province',
    'city',
    'state',
    'commissionsRecurrent',
    'commissionsSubscription',
  ];
  const bankFields = [
    'bankName',
    'documentNumber',
    'companyType',
    'transferInterval',
    'transferDay',
    'accountType',
    'bankCode',
    'bankAg',
    'bankCc',
  ];

  if (Object.keys(errors).some((key) => personalFields.includes(key))) return 'personal';
  if (Object.keys(errors).some((key) => addressFields.includes(key))) return 'address';
  if (Object.keys(errors).some((key) => bankFields.includes(key))) return 'bank';
  return 'personal';
};

const AccountForm = ({ mode, employee, accessProfiles, activeTab, onTabChange, onCancel, onSaved }) => {
  const [form, setForm] = useState(() => buildInitialForm(employee, mode));
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  const areaOptions = useMemo(() => {
    const areas = accessProfiles.map((profile) => profileArea(profile)).filter(Boolean);
    return [...new Set(areas)].map((area) => ({ value: area, label: areaLabels[area] || area }));
  }, [accessProfiles]);

  const profileOptions = useMemo(
    () =>
      accessProfiles
        .filter((profile) => profileArea(profile) === form.area)
        .map((profile) => ({
          value: String(profile.id),
          label: profile.adminMaster ? profile.name : jobTitleLabels[profile.jobTitle] || profile.name,
        })),
    [accessProfiles, form.area]
  );

  const setValue = (name, value) => {
    setForm((currentForm) => ({ ...currentForm, [name]: value }));
    setErrors((currentErrors) => ({ ...currentErrors, [name]: '' }));
  };

  const setBankValue = (name, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      bankAccount: {
        ...currentForm.bankAccount,
        [name]: value,
        ...(name === 'documentNumber' && String(value).replace(/\D/g, '').length !== 14 ? { companyType: '' } : {}),
        ...(name === 'transferInterval' && value === 'daily' ? { transferDay: '' } : {}),
      },
    }));

    const errorName = name === 'name' ? 'bankName' : name;
    setErrors((currentErrors) => ({
      ...currentErrors,
      [errorName]: '',
      ...(name === 'documentNumber' ? { companyType: '' } : {}),
      ...(name === 'transferInterval' && value === 'daily' ? { transferDay: '' } : {}),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateForm(form, mode);

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      onTabChange(errorTab(nextErrors));
      setServerError('Revise os campos destacados antes de salvar.');
      return;
    }

    const { firstName, lastName } = splitName(form.fullName);
    const selectedProfile = accessProfiles.find((profile) => String(profile.id) === form.accessProfileId);
    const bank = form.bankAccount;
    const employeePayload = {
      first_name: firstName,
      last_name: lastName,
      email: form.email.trim(),
      cpf: form.cpf || '',
      phone: form.phone,
      birth_date: form.birthDate,
      income_value: form.incomeValue,
      role: roleForProfile(selectedProfile, employee),
      access_profile_id: form.accessProfileId,
      postal_code: String(form.postalCode).replace(/\D/g, ''),
      address: form.address,
      address_number: form.addressNumber,
      province: form.province,
      city: form.city,
      state: form.state.toUpperCase(),
      commissions_recurrent: form.commissionsRecurrent,
      commissions_subscription: form.commissionsSubscription,
      payment_account_attributes: {
        id: bank.id,
        name: bank.name,
        document_number: bank.documentNumber,
        company_type: bank.companyType || null,
        transfer_interval: bank.transferInterval,
        transfer_day: bank.transferInterval === 'daily' ? null : bank.transferDay,
        account_type: bank.accountType,
        bank_code: bank.bankCode,
        bank_ag: bank.bankAg,
        agencia_dv: bank.agenciaDv,
        bank_cc: bank.bankCc,
        conta_dv: bank.contaDv,
      },
    };

    if (mode === 'create') {
      employeePayload.password = form.password;
      employeePayload.password_confirmation = form.password;
    }

    setSaving(true);
    setServerError('');

    try {
      const endpoint = mode === 'create' ? '/app/master/employees' : `/app/master/employees/${employee.id}`;
      const response = await fetchWithCsrf(endpoint, {
        method: mode === 'create' ? 'POST' : 'PATCH',
        headers: { Accept: 'application/json' },
        body: JSON.stringify({ employee: employeePayload }),
      });
      const payload = camelizeKeys(await response.json());

      if (!response.ok) throw new Error(payload.errors?.join(' ') || 'Não foi possível salvar a conta.');

      addToast({
        type: 'success',
        message: mode === 'create' ? 'Conta adicionada com sucesso.' : 'Conta atualizada com sucesso.',
      });
      onSaved(payload.employee);
    } catch (error) {
      setServerError(error.message);
      addToast({ type: 'error', message: error.message });
    } finally {
      setSaving(false);
    }
  };

  const bank = form.bankAccount;

  return (
    <form className="flex flex-1 flex-col" onSubmit={handleSubmit} noValidate>
      <div className="flex-1 py-6">
        {serverError && (
          <div className="mb-5 rounded-xl border border-redExtraLight bg-redExtraLight2 px-4 py-3 text-sm text-redBase">
            {serverError}
          </div>
        )}

        {activeTab === 'personal' && (
          <div className="grid grid-cols-1 gap-x-4 gap-y-6 md:grid-cols-2 lg:grid-cols-3">
            <SectionTitle>Dados pessoais</SectionTitle>
            <TextField
              label="Nome"
              name="fullName"
              value={form.fullName}
              onChange={(value) => setValue('fullName', value)}
              placeholder="Ex.: João da Silva"
              error={errors.fullName}
            />
            <TextField
              label="E-mail"
              name="email"
              type="email"
              value={form.email}
              onChange={(value) => setValue('email', value)}
              placeholder="nome@rioclaro.com.br"
              error={errors.email}
            />
            <MaskedField
              label="CPF"
              name="cpf"
              value={form.cpf}
              onChange={(value) => setValue('cpf', value)}
              placeholder="000.000.000-00"
              error={errors.cpf}
              mask="000.000.000-00"
            />
            <DateField
              value={form.birthDate}
              onChange={(value) => setValue('birthDate', value)}
              error={errors.birthDate}
            />
            <MaskedField
              label="Telefone"
              name="phone"
              value={form.phone}
              onChange={(value) => setValue('phone', value)}
              placeholder="(00) 00000-0000"
              error={errors.phone}
              mask="(00) 00000-0000"
            />
            <CurrencyField
              value={form.incomeValue}
              onChange={(value) => setValue('incomeValue', value)}
              error={errors.incomeValue}
            />

            <SectionTitle>Acesso</SectionTitle>
            <SelectField
              label="Área"
              name="area"
              value={form.area}
              onChange={(value) => {
                setValue('area', value);
                setValue('accessProfileId', '');
              }}
              options={areaOptions}
              placeholder="Selecione a área"
              error={errors.area}
            />
            <SelectField
              label="Cargo"
              name="accessProfileId"
              value={form.accessProfileId}
              onChange={(value) => setValue('accessProfileId', value)}
              options={profileOptions}
              placeholder="Selecione o cargo"
              error={errors.accessProfileId}
              disabled={!form.area}
            />
            {mode === 'create' && (
              <Field label="Senha temporária de acesso" name="password" error={errors.password}>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type="text"
                    value={form.password}
                    onChange={(event) => setValue('password', event.target.value)}
                    className={`${fieldClassName(errors.password)} pr-11`}
                  />
                  <button
                    type="button"
                    aria-label="Gerar uma nova senha temporária"
                    title="Gerar nova senha"
                    onClick={() => setValue('password', generateTemporaryPassword())}
                    className="absolute inset-y-0 right-3 flex items-center text-tealMedium transition-colors hover:text-tealDeep"
                  >
                    <RefreshCw size={18} strokeWidth={1.7} />
                  </button>
                </div>
              </Field>
            )}
          </div>
        )}

        {activeTab === 'address' && (
          <div className="grid grid-cols-1 gap-x-4 gap-y-6 md:grid-cols-2 lg:grid-cols-3">
            <SectionTitle>Endereço</SectionTitle>
            <MaskedField
              label="CEP"
              name="postalCode"
              value={form.postalCode}
              onChange={(value) => setValue('postalCode', value)}
              placeholder="00000-000"
              error={errors.postalCode}
              mask="00000-000"
            />
            <TextField
              label="Logradouro"
              name="address"
              value={form.address}
              onChange={(value) => setValue('address', value)}
              placeholder="Rua / Avenida"
              error={errors.address}
            />
            <TextField
              label="Número"
              name="addressNumber"
              value={form.addressNumber}
              onChange={(value) => setValue('addressNumber', value)}
              placeholder="Nº"
              error={errors.addressNumber}
            />
            <TextField
              label="Bairro"
              name="province"
              value={form.province}
              onChange={(value) => setValue('province', value)}
              placeholder="Bairro"
              error={errors.province}
            />
            <TextField
              label="Cidade"
              name="city"
              value={form.city}
              onChange={(value) => setValue('city', value)}
              placeholder="Cidade"
              error={errors.city}
            />
            <TextField
              label="UF"
              name="state"
              value={form.state}
              onChange={(value) => setValue('state', value.toUpperCase())}
              placeholder="UF"
              error={errors.state}
              maxLength={2}
            />

            <SectionTitle>Comissão</SectionTitle>
            <PercentageField
              label="Comissão Pgto Recorrente (%)"
              name="commissionsRecurrent"
              value={form.commissionsRecurrent}
              onChange={(value) => setValue('commissionsRecurrent', value)}
              error={errors.commissionsRecurrent}
            />
            <PercentageField
              label="Comissão Pgto Inscrição (%)"
              name="commissionsSubscription"
              value={form.commissionsSubscription}
              onChange={(value) => setValue('commissionsSubscription', value)}
              error={errors.commissionsSubscription}
            />
          </div>
        )}

        {activeTab === 'bank' && (
          <div className="grid grid-cols-1 gap-x-4 gap-y-6 md:grid-cols-2 lg:grid-cols-3">
            <SectionTitle>Dados bancários · Repasse de comissão</SectionTitle>
            <TextField
              label="Nome completo (titular)"
              name="bankName"
              value={bank.name}
              onChange={(value) => setBankValue('name', value)}
              placeholder="Nome do titular da conta"
              error={errors.bankName}
            />
            <MaskedField
              label="CPF/CNPJ"
              name="documentNumber"
              value={bank.documentNumber}
              onChange={(value) => setBankValue('documentNumber', value)}
              placeholder="000.000.000-00"
              error={errors.documentNumber}
              mask={['000.000.000-00', '00.000.000/0000-00']}
            />
            <SelectField
              label="Company type"
              name="companyType"
              value={bank.companyType || ''}
              onChange={(value) => setBankValue('companyType', value)}
              options={companyTypeOptions}
              placeholder="Selecione"
              error={errors.companyType}
              hint="Preencher se o documento for um CNPJ."
            />
            <SelectField
              label="Intervalo de transferência"
              name="transferInterval"
              value={bank.transferInterval || ''}
              onChange={(value) => setBankValue('transferInterval', value)}
              options={transferIntervalOptions}
              placeholder="Diário / Semanal / Mensal"
              error={errors.transferInterval}
            />
            <TextField
              label="Dia da transferência"
              name="transferDay"
              value={bank.transferDay || ''}
              onChange={(value) => setBankValue('transferDay', value.replace(/\D/g, ''))}
              placeholder="—"
              error={errors.transferDay}
              maxLength={2}
              disabled={bank.transferInterval === 'daily'}
              hint="Semanal: 1 (seg.) a 5 (sex.). Mensal: 1 a 31. Diário: não necessário."
            />
            <SelectField
              label="Tipo de conta"
              name="accountType"
              value={bank.accountType || ''}
              onChange={(value) => setBankValue('accountType', value)}
              options={accountTypeOptions}
              placeholder="Corrente / Poupança"
              error={errors.accountType}
            />
            <TextField
              label="Cód. do banco"
              name="bankCode"
              value={bank.bankCode || ''}
              onChange={(value) => setBankValue('bankCode', value.replace(/\D/g, ''))}
              placeholder="000"
              error={errors.bankCode}
              maxLength={3}
            />
            <TextField
              label="Agência"
              name="bankAg"
              value={bank.bankAg || ''}
              onChange={(value) => setBankValue('bankAg', value)}
              placeholder="0000"
              error={errors.bankAg}
            />
            <TextField
              label="Dígito verif. da agência"
              name="agenciaDv"
              value={bank.agenciaDv || ''}
              onChange={(value) => setBankValue('agenciaDv', value)}
              placeholder="0"
              error={errors.agenciaDv}
            />
            <TextField
              label="Nº da conta"
              name="bankCc"
              value={bank.bankCc || ''}
              onChange={(value) => setBankValue('bankCc', value)}
              placeholder="00000-0"
              error={errors.bankCc}
            />
            <TextField
              label="Dígito verif. da conta"
              name="contaDv"
              value={bank.contaDv || ''}
              onChange={(value) => setBankValue('contaDv', value)}
              placeholder="0"
              error={errors.contaDv}
            />
          </div>
        )}
      </div>

      <footer className="mt-5 flex justify-end gap-5">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={{ minWidth: '122px' }}
          className="rounded-xl border-b-2 border-r-2 border-graySemiDark bg-blueUltraLight px-6 py-3 text-sm font-semibold text-grayDark transition-colors hover:bg-grayLight disabled:cursor-not-allowed disabled:opacity-60"
        >
          CANCELAR
        </button>
        <button
          type="submit"
          disabled={saving}
          style={{ minWidth: '100px' }}
          className="rounded-xl border-b-2 border-r-2 border-tealBase bg-tealMedium px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'SALVANDO...' : 'SALVAR'}
        </button>
      </footer>
    </form>
  );
};

AccountForm.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  employee: PropTypes.object,
  accessProfiles: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

AccountForm.defaultProps = {
  employee: null,
};

export default AccountForm;
